
import { Component, Vue } from 'vue-property-decorator'
import { queryChartData } from '@/api/query'
import echart, { ECharts } from 'echarts'

import { drawMapChart } from '@/charts/platform/map_chart'
import { drawPieChart } from '@/charts/platform/pie_chart'
import { drawSpendingAllChart } from '@/charts/platform/spending_all'
import { drawSpendingBarChart } from '@/charts/platform/spending_bar'
import { drawCompanySpendingBarChart } from '@/charts/platform/company_spending_bar'
import { drawCompanyRechargeBarChart } from '@/charts/platform/company_recharge_bar'
import { drawCompanyBalanceBarChart } from '@/charts/platform/company_balance_bar'

@Component({
  name: 'PlaformChart'
})
export default class extends Vue {
  protected cardTotalData = '统计中...'
  protected driverTotalData = '统计中...'

  private loading = false
  private requestId = 0
  private chartInstances: { [key: string]: ECharts | null } = {
    mapChart: null,
    spendingPieChart: null,
    spendingAllChart: null,
    spendingBarChart: null,
    companySpendingBarChart: null,
    companyRechargeBarChart: null,
    companyBalanceBarChart: null
  }

  private mapData: any[] = []
  private spendingPieData: any[] = []
  private spendingAllData: any[] = []
  private spendingBarData: any[] = []
  private companySpendingBarChartData: any[] = []
  private companyRechargeBarChartData: any[] = []
  private companyBalanceBarChartData: any[] = []

  private resizeHandler = () => {
    Object.keys(this.chartInstances).forEach((key) => {
      const chart = this.chartInstances[key]
      if (chart) {
        chart.resize()
      }
    })
  }

  mounted() {
    this.initCharts()
    window.addEventListener('resize', this.resizeHandler)
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
    Object.keys(this.chartInstances).forEach((key) => {
      const chart = this.chartInstances[key]
      if (chart) {
        chart.dispose()
      }
      this.chartInstances[key] = null
    })
  }

  public async fetchChartsData(filters: any, _id?: any) {
    const currentRequestId = ++this.requestId
    this.loading = true
    try {
      const [
        mapData,
        driverTotal,
        cardTotal,
        spendingPieData,
        spendingAllData,
        spendingBarData,
        companySpendingBarChartData,
        companyRechargeBarChartData,
        companyBalanceBarChartData
      ] = await Promise.all([
        queryChartData('/counts/all/station', filters),
        queryChartData('/counts/all/driver', {}),
        queryChartData('/counts/all/card', {}),
        queryChartData('/counts/all/spending/pie', filters),
        queryChartData('/counts/all/spending', filters),
        queryChartData('/counts/all/station/spending/bar', filters),
        queryChartData('/counts/all/company/spending/bar', filters),
        queryChartData('/counts/all/company/recharge/bar', filters),
        queryChartData('/counts/all/company', filters)
      ])

      if (currentRequestId !== this.requestId) {
        return
      }

      this.mapData = mapData || []
      this.spendingPieData = spendingPieData || []
      this.spendingAllData = spendingAllData || []
      this.spendingBarData = spendingBarData || []
      this.companySpendingBarChartData = companySpendingBarChartData || []
      this.companyRechargeBarChartData = companyRechargeBarChartData || []
      this.companyBalanceBarChartData = companyBalanceBarChartData || []
      this.driverTotalData = this.formatCurrency(driverTotal && driverTotal[0] ? driverTotal[0].total : 0)
      this.cardTotalData = this.formatCurrency(cardTotal && cardTotal[0] ? cardTotal[0].total : 0)

      this.drawCharts(filters)
    } finally {
      if (currentRequestId === this.requestId) {
        this.loading = false
      }
    }
  }

  private drawCharts(filters: any) {
    drawMapChart(this.chartInstances.mapChart as ECharts, this.mapData, filters)
    drawPieChart(this.chartInstances.spendingPieChart as ECharts, this.spendingPieData, filters)
    drawSpendingAllChart(this.chartInstances.spendingAllChart as ECharts, this.spendingAllData, filters)
    drawSpendingBarChart(this.chartInstances.spendingBarChart as ECharts, this.spendingBarData, filters)
    drawCompanySpendingBarChart(
      this.chartInstances.companySpendingBarChart as ECharts,
      this.companySpendingBarChartData,
      filters
    )
    drawCompanyRechargeBarChart(
      this.chartInstances.companyRechargeBarChart as ECharts,
      this.companyRechargeBarChartData,
      filters
    )
    drawCompanyBalanceBarChart(
      this.chartInstances.companyBalanceBarChart as ECharts,
      this.companyBalanceBarChartData,
      filters
    )
  }

  private initCharts() {
    this.chartInstances.mapChart = this.createChart(this.$refs.mapChartRef as HTMLDivElement)
    this.chartInstances.spendingPieChart = this.createChart(this.$refs.spendingPieChartRef as HTMLDivElement)
    this.chartInstances.spendingAllChart = this.createChart(this.$refs.spendingAllChartRef as HTMLDivElement)
    this.chartInstances.spendingBarChart = this.createChart(this.$refs.spendingBarChartRef as HTMLDivElement)
    this.chartInstances.companySpendingBarChart = this.createChart(this.$refs.companySpendingBarChartRef as HTMLDivElement)
    this.chartInstances.companyRechargeBarChart = this.createChart(this.$refs.companyRechargeBarChartRef as HTMLDivElement)
    this.chartInstances.companyBalanceBarChart = this.createChart(this.$refs.companyBalanceBarChartRef as HTMLDivElement)
  }

  private createChart(element: HTMLDivElement) {
    return echart.getInstanceByDom(element) || echart.init(element)
  }

  private formatCurrency(value: any) {
    const amount = Number(value || 0)
    return `${amount.toFixed(2)}￥`
  }
}
